import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ComboboxOption } from '@elbklar/ui';
import { customersApi, leadsApi, partnersApi } from '@/lib/api';

export type EntityType = 'customer' | 'partner';

/**
 * Serversuche für die Combobox aus @elbklar/ui: Kunde oder Partner über
 * GET /customers bzw. /partners?search=…, je zehn Treffer. Das Ergebnis passt
 * direkt auf die Combobox: `<Combobox {...useAuswahlSuche('customer', wert)} … />`.
 *
 * Bei „Kunde“ kommen Leads mit: Ein Lead ist ein Kunde im Lead-Status, aber
 * GET /customers lässt Leads aus. Ohne sie ließ sich eine Notiz, ein Anruf
 * oder eine Aufgabe keinem Lead zuordnen. Leads stehen nach den Kunden, mit
 * „Lead“ vor der Nummer.
 */
export function useAuswahlSuche(art: EntityType, value: string) {
  // null, solange die Auswahl noch nie offen war: dann wird nichts geladen.
  const [begriff, setBegriff] = useState<string | null>(null);
  const bereich = art === 'customer' ? 'customers' : 'partners';

  const { data: options = [], isFetching } = useQuery({
    queryKey: [bereich, 'search', begriff],
    queryFn: async (): Promise<ComboboxOption[]> => {
      const search = begriff || undefined;
      if (art === 'customer') {
        const [kunden, leads] = await Promise.all([
          customersApi.list({ search, per_page: 10 }),
          leadsApi.list({ search, per_page: 10 }),
        ]);
        return [
          ...kunden.items.map((c) => ({ value: c.id, label: c.company_name, detail: c.customer_number || undefined })),
          ...leads.items.map((l) => ({ value: l.id, label: l.company_name, detail: l.customer_number ? `Lead ${l.customer_number}` : 'Lead' })),
        ];
      }
      const res = await partnersApi.list({ search, per_page: 10 });
      return res.items.map((p) => ({ value: p.id, label: p.name, detail: p.partner_number || undefined }));
    },
    enabled: begriff !== null,
    placeholderData: keepPreviousData,
  });

  // Name eines vorgegebenen Werts (aus der URL, vom offenen Kunden), solange
  // er nicht unter den Treffern steht.
  const { data: selectedLabel } = useQuery({
    queryKey: [bereich, value, 'name'],
    queryFn: async () => (art === 'customer' ? (await customersApi.get(value)).company_name : (await partnersApi.get(value)).name),
    enabled: !!value && !options.some((o) => o.value === value),
    staleTime: Infinity,
  });

  return { options, loading: isFetching, onSearch: setBegriff, selectedLabel };
}
